import { For, Show, createMemo, createSignal, onCleanup } from 'solid-js';
import type { Square } from '@domain/chess/types';
import { gameStore } from '@state/gameStore';
import {
  attemptMove,
  confirmPromotion,
  gameOutcome,
  selectSquare,
  snapshotPieces,
} from '@state/actions';
import type { Color, Role } from '@domain/chess/types';
import { flags } from '@config/flags';
import { pieceRelations, relationsFromPiece } from '@domain/analysis/pieceRelations';
import { computeAllOverlays } from '@overlays/registry';
import { lastMoveTurnsFromHistory } from '@domain/chess/moveTurns';
import { TurnStack } from '@ui/TurnStack/TurnStack';
import { pieceUrl } from './pieceUrls';
import './Board.css';

const PROMO_ROLES = ['q', 'r', 'b', 'n'] as const satisfies readonly Role[];

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;
const RANKS = [8, 7, 6, 5, 4, 3, 2, 1] as const;

function isLight(file: string, rank: number): boolean {
  return (file.charCodeAt(0) + rank) % 2 === 1;
}

function squareToGridWhiteBottom(sq: Square) {
  const file = sq.charCodeAt(0) - 97;
  const rank = Number(sq[1]) - 1;
  return { col: file, row: 7 - rank };
}

/** Match visible attacked rings; intensity 1 is the default (invisible) ring. */
function ringSpec(intensity?: number) {
  const n = Math.max(1, Math.min(intensity ?? 1, 6));
  const r = 0.4 + n * 0.02;
  const stroke = 0.05 + n * 0.012;
  // +0.04 keeps the 5px line stroke outside the ring instead of overlapping it.
  return { n, r, stroke, outer: r + stroke / 2 + 0.04 };
}

function clipLineToRings(
  ax: number,
  ay: number,
  ra: number,
  bx: number,
  by: number,
  rb: number,
): { x1: number; y1: number; x2: number; y2: number } | null {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy);
  if (len < ra + rb + 0.01) return null;
  const ux = dx / len;
  const uy = dy / len;
  return {
    x1: ax + ux * ra,
    y1: ay + uy * ra,
    x2: bx - ux * rb,
    y2: by - uy * rb,
  };
}

function squareFromPoint(clientX: number, clientY: number): Square | null {
  // Prefer squares under cursor; pieces also carry data-square for captures.
  // Temporarily ignore the drag ghost (pointer-events: none already).
  const stack =
    typeof document.elementsFromPoint === 'function'
      ? document.elementsFromPoint(clientX, clientY)
      : ([document.elementFromPoint(clientX, clientY)].filter(Boolean) as Element[]);

  for (const el of stack) {
    if (!(el instanceof Element)) continue;
    if (el.classList.contains('board-drag-ghost')) continue;
    const hit = el.closest('[data-square]') as HTMLElement | null;
    const sq = hit?.dataset.square;
    if (sq) return sq as Square;
  }
  return null;
}

type DragState = {
  from: Square;
  src: string;
  x: number;
  y: number;
  moved: boolean;
};

export function Board() {
  const [drag, setDrag] = createSignal<DragState | null>(null);
  let suppressClick = false;

  const orderedSquares = createMemo(() => {
    const list: Square[] = [];
    for (const rank of RANKS) {
      for (const file of FILES) {
        list.push(`${file}${rank}` as Square);
      }
    }
    return list;
  });

  const pieceViews = createMemo(() => {
    if (gameStore.interaction === 'mark') {
      return gameStore.puzzlePieces.map((p) => {
        const { col, row } = squareToGridWhiteBottom(p.square);
        return {
          sq: p.square,
          piece: { color: p.color, role: p.role },
          left: (col / 8) * 100,
          top: (row / 8) * 100,
        };
      });
    }
    void gameStore.fen;
    const map = snapshotPieces().pieces;
    return [...map.entries()].map(([sq, piece]) => {
      const { col, row } = squareToGridWhiteBottom(sq);
      return {
        sq,
        piece,
        left: (col / 8) * 100,
        top: (row / 8) * 100,
      };
    });
  });

  const outcome = createMemo(() => {
    void gameStore.fen;
    void gameStore.cursor;
    return gameOutcome();
  });

  const revealed = () => Boolean(gameStore.loadedOpeningName);

  const awareness = createMemo(() => {
    if (!flags.awarenessOverlays || !revealed() || !gameStore.selected) {
      return { squares: [], labels: [] };
    }
    void gameStore.selected;
    void gameStore.fen;
    const board = snapshotPieces();
    return computeAllOverlays({ board, options: {} });
  });

  const relations = createMemo(() => {
    if (!flags.awarenessOverlays || !revealed() || !gameStore.selected) return [];
    void gameStore.fen;
    return relationsFromPiece(pieceRelations(gameStore.fen), gameStore.selected);
  });

  const attackedIntensity = createMemo(() => {
    const map = new Map<Square, number>();
    for (const s of awareness().squares ?? []) {
      if (s.className !== 'overlay-attacked' && s.className !== 'overlay-unprotected') continue;
      map.set(s.sq, Math.max(1, Math.min(s.intensity ?? 1, 6)));
    }
    return map;
  });

  const ghostRingSquares = createMemo(() => {
    const attacked = attackedIntensity();
    const seen = new Set<Square>();
    const out: Square[] = [];
    for (const rel of relations()) {
      for (const sq of [rel.from, rel.to]) {
        if (attacked.has(sq) || seen.has(sq)) continue;
        seen.add(sq);
        out.push(sq);
      }
    }
    return out;
  });

  const moveTurns = createMemo(() => {
    void gameStore.fen;
    void gameStore.cursor;
    return lastMoveTurnsFromHistory(
      gameStore.startFen,
      gameStore.history.slice(0, gameStore.cursor),
    );
  });

  function turnColor(): 'w' | 'b' {
    // Adapter is source of truth for side to move (not fragile string includes)
    void gameStore.fen;
    return snapshotPieces().turn;
  }

  function canDragPiece(color: 'w' | 'b'): boolean {
    if (gameStore.interaction === 'mark') return false;
    return (
      outcome() === 'playing' &&
      !gameStore.pendingPromotion &&
      color === turnColor()
    );
  }

  function kingFallen(color: 'w' | 'b'): boolean {
    const o = outcome();
    if (o === 'draw') return true;
    if (o === 'checkmate') return color === turnColor();
    return false;
  }

  function endDrag(clientX: number, clientY: number) {
    const d = drag();
    setDrag(null);
    document.body.classList.remove('chess-dragging');

    if (!d) return;

    if (!d.moved) {
      // treat as click selection only (already selected on pointerdown)
      return;
    }

    suppressClick = true;
    queueMicrotask(() => {
      suppressClick = false;
    });

    const to = squareFromPoint(clientX, clientY);
    if (!to || to === d.from) {
      // Keep selection if it was only a tiny drag; clear if cancelled off-board
      if (!to) selectSquare(null);
      return;
    }
    attemptMove(d.from, to);
  }

  function onPointerMove(e: PointerEvent) {
    const d = drag();
    if (!d) return;
    const moved =
      d.moved || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 4;
    setDrag({
      ...d,
      x: e.clientX,
      y: e.clientY,
      moved,
    });
  }

  function onPointerUp(e: PointerEvent) {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
    endDrag(e.clientX, e.clientY);
  }

  function onPiecePointerDown(
    e: PointerEvent,
    sq: Square,
    color: 'w' | 'b',
    src: string,
  ) {
    if (e.button !== 0) return;
    if (!canDragPiece(color)) return;

    e.preventDefault();
    selectSquare(sq);
    setDrag({ from: sq, src, x: e.clientX, y: e.clientY, moved: false });
    document.body.classList.add('chess-dragging');

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  }

  onCleanup(() => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
    document.body.classList.remove('chess-dragging');
  });

  return (
    <div class="board-wrap">
      <div
        class="board-root"
        classList={{
          'board-root--black': gameStore.orientation === 'black',
          'board-root--dragging': Boolean(drag()?.moved),
        }}
        role="grid"
        aria-label="Chess board"
      >
        <div class="board-squares" role="presentation">
          <For each={orderedSquares()}>
            {(sq) => {
              const file = sq[0]!;
              const rank = Number(sq[1]);
              const light = isLight(file, rank);
              const d = drag();
              return (
                <button
                  type="button"
                  class="board-sq"
                  classList={{
                    'board-sq--light': light,
                    'board-sq--dark': !light,
                    'board-sq--selected': gameStore.selected === sq,
                    'board-sq--marked': gameStore.markedSquares.includes(sq),
                    'board-sq--marking': gameStore.interaction === 'mark',
                    'board-sq--last':
                      gameStore.interaction === 'play' &&
                      (gameStore.lastMove?.from === sq || gameStore.lastMove?.to === sq),
                    'board-sq--legal':
                      gameStore.interaction === 'play' &&
                      gameStore.legalTargets.includes(sq),
                    'board-sq--drop':
                      d !== null &&
                      d.moved &&
                      gameStore.legalTargets.includes(sq),
                  }}
                  data-square={sq}
                  aria-label={sq}
                  onClick={() => {
                    if (suppressClick) return;
                    selectSquare(sq);
                  }}
                >
                  <Show when={gameStore.interaction === 'play' && moveTurns().get(sq)}>
                    {(stack) => <TurnStack turns={stack()} />}
                  </Show>
                </button>
              );
            }}
          </For>
        </div>

        <svg class="board-marks" viewBox="0 0 8 8" aria-hidden="true">
          <For each={gameStore.revealMarks}>
            {(item) => {
              const { col, row } = squareToGridWhiteBottom(item.sq);
              const cx = col + 0.5;
              const cy = row + 0.5;
              const flip = gameStore.orientation === 'black';
              const transform = flip
                ? `translate(${cx} ${cy}) rotate(180)`
                : `translate(${cx} ${cy})`;
              return (
                <g class={`board-mark board-mark--${item.mark}`} transform={transform}>
                  <Show when={item.mark === 'correct'}>
                    <path d="M-0.28 0.02 L-0.08 0.22 L0.3 -0.22" />
                  </Show>
                  <Show when={item.mark === 'wrong'}>
                    <path d="M-0.22 -0.22 L0.22 0.22" />
                    <path d="M0.22 -0.22 L-0.22 0.22" />
                  </Show>
                  <Show when={item.mark === 'missing'}>
                    <text text-anchor="middle" dominant-baseline="central">
                      ?
                    </text>
                  </Show>
                </g>
              );
            }}
          </For>
        </svg>

        <svg class="board-overlays" viewBox="0 0 8 8" aria-hidden="true">
          <For each={awareness().squares ?? []}>
            {(s) => {
              const { col, row } = squareToGridWhiteBottom(s.sq);
              const spec = ringSpec(s.intensity);
              return (
                <g class={s.className} data-count={spec.n}>
                  <circle
                    cx={col + 0.5}
                    cy={row + 0.5}
                    r={spec.r}
                    class={`${s.className}__ring`}
                    style={{ 'stroke-width': String(spec.stroke) }}
                  />
                </g>
              );
            }}
          </For>
          <For each={ghostRingSquares()}>
            {(sq) => {
              const { col, row } = squareToGridWhiteBottom(sq);
              const spec = ringSpec();
              return (
                <circle
                  cx={col + 0.5}
                  cy={row + 0.5}
                  r={spec.r}
                  class="overlay-attacked__ring overlay-attacked__ring--ghost"
                  style={{ 'stroke-width': String(spec.stroke) }}
                />
              );
            }}
          </For>
          <For each={gameStore.interaction === 'play' ? gameStore.legalTargets : []}>
            {(sq) => {
              const { col, row } = squareToGridWhiteBottom(sq);
              const x = col + 0.5;
              const y = row + 0.5;
              const occupied = pieceViews().some((p) => p.sq === sq);
              return occupied ? (
                <circle cx={x} cy={y} r="0.42" class="legal-ring" />
              ) : (
                <circle cx={x} cy={y} r="0.14" class="legal-dot" />
              );
            }}
          </For>
        </svg>

        <div class="board-pieces" role="presentation">
          <For each={pieceViews()}>
            {(item) => {
              const movable = () => canDragPiece(item.piece.color);
              const dragging = () => drag()?.from === item.sq && drag()!.moved;
              const src = pieceUrl(item.piece.color, item.piece.role);
              return (
                <div
                  class="board-piece-stack"
                  classList={{
                    'board-piece-stack--selected': gameStore.selected === item.sq,
                    'board-piece-stack--movable': movable(),
                    'board-piece-stack--dragging': dragging(),
                  }}
                  data-square={item.sq}
                  style={{
                    left: `${item.left}%`,
                    top: `${item.top}%`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (suppressClick) return;
                    selectSquare(item.sq);
                  }}
                  onPointerDown={(e) =>
                    onPiecePointerDown(e, item.sq, item.piece.color, src)
                  }
                >
                  <img
                    class="board-piece"
                    classList={{
                      'board-piece--fallen':
                        item.piece.role === 'k' && kingFallen(item.piece.color),
                    }}
                    src={src}
                    alt={`${item.piece.color === 'w' ? 'White' : 'Black'} ${item.piece.role}`}
                    draggable={false}
                  />
                  <Show when={gameStore.revealCorrectSquares.includes(item.sq)}>
                    <svg class="board-piece-check" viewBox="0 0 100 100" aria-hidden="true">
                      <path d="M14 53 39 78 86 20" />
                    </svg>
                  </Show>
                </div>
              );
            }}
          </For>
        </div>

        <svg class="board-labels" viewBox="0 0 8 8" aria-hidden="true">
          <For each={awareness().labels ?? []}>
            {(lab) => {
              const { col, row } = squareToGridWhiteBottom(lab.sq);
              const pos = lab.position ?? 'top-right';
              const targetX =
                pos === 'top-left' || pos === 'bottom-left' ? 0.22 : 0.78;
              const targetY =
                pos === 'bottom-right' || pos === 'bottom-left' ? 0.78 : 0.22;

              const isBlack = () => gameStore.orientation === 'black';
              const bx = () => (isBlack() ? col + (1 - targetX) : col + targetX);
              const by = () => (isBlack() ? row + (1 - targetY) : row + targetY);
              const transform = () =>
                isBlack()
                  ? `translate(${bx()} ${by()}) rotate(180)`
                  : `translate(${bx()} ${by()})`;

              return (
                <g
                  class={`overlay-attacked__badge ${lab.className ?? ''}`}
                  transform={transform()}
                >
                  <circle r="0.18" class="overlay-attacked__badge-bg" />
                  <text
                    class="overlay-attacked__badge-text"
                    text-anchor="middle"
                    dominant-baseline="central"
                  >
                    {lab.text}
                  </text>
                </g>
              );
            }}
          </For>
        </svg>

        <svg class="board-relations" viewBox="0 0 8 8" aria-hidden="true">
          <For each={relations()}>
            {(rel) => {
              const a = squareToGridWhiteBottom(rel.from);
              const b = squareToGridWhiteBottom(rel.to);
              const hit = attackedIntensity();
              const clipped = clipLineToRings(
                a.col + 0.5,
                a.row + 0.5,
                ringSpec(hit.get(rel.from)).outer,
                b.col + 0.5,
                b.row + 0.5,
                ringSpec(hit.get(rel.to)).outer,
              );
              return (
                <Show when={clipped}>
                  {(c) => (
                    <line
                      x1={c().x1}
                      y1={c().y1}
                      x2={c().x2}
                      y2={c().y2}
                      class={`rel-line rel-line--${rel.kind}`}
                    />
                  )}
                </Show>
              );
            }}
          </For>
        </svg>
      </div>

      {/* Floating ghost under cursor — keeps hand cursor (native DnD cannot) */}
      <Show when={drag() && drag()!.moved}>
        <img
          class="board-drag-ghost"
          src={drag()!.src}
          alt=""
          style={{
            left: `${drag()!.x}px`,
            top: `${drag()!.y}px`,
          }}
        />
      </Show>

      <Show when={gameStore.pendingPromotion}>
        {(() => {
          // Promoting side is still to move (move not applied yet)
          const color: Color = gameStore.fen.includes(' w ') ? 'w' : 'b';
          const names: Record<(typeof PROMO_ROLES)[number], string> = {
            q: 'Queen',
            r: 'Rook',
            b: 'Bishop',
            n: 'Knight',
          };
          return (
            <div class="promo" role="dialog" aria-label="Choose promotion piece">
              <p class="promo__label">Promote to</p>
              <div class="promo__choices">
                <For each={[...PROMO_ROLES]}>
                  {(role) => (
                    <button
                      type="button"
                      class="promo__btn"
                      title={`${names[role]} (${role.toUpperCase()})`}
                      aria-label={names[role]}
                      onClick={() => confirmPromotion(role)}
                    >
                      <img
                        class="promo__piece"
                        src={pieceUrl(color, role)}
                        alt={names[role]}
                        draggable={false}
                      />
                    </button>
                  )}
                </For>
              </div>
            </div>
          );
        })()}
      </Show>
    </div>
  );
}
